import { Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { TurnoverDto } from '@tom-ui/integration'
import {
  EmptyIndicator,
  TableDateCell,
  TomTable,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { ContainerHistoryRowActions } from '../Atoms/ContainerHistoryRowActions'

type Props = {
  containerTurnoverList: TurnoverDto[]
}

export const ContainerHistoryTable: FC<Props> = observer(({ containerTurnoverList }) => {
  const { t } = useTranslate()

  const columns = useMemo<MRT_ColumnDef<TurnoverDto>[]>(
    () => [
      {
        accessorKey: 'InboundCarrierVisit.CarrierName',
        header: t('inboundCarrier', 'Inbound carrier'),
        Cell: cell => <Typography variant='subtitle2'>{cell.renderedCellValue}</Typography>,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'InboundCarrierVisit.VesselVisitNumber',
        header: t('inboundCarrierId', 'Inbound carrier ID'),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'TerminalEntry',
        header: t('timeIn', 'Time in'),
        Cell: cell => <TableDateCell date={cell.row.original.TerminalEntry} />,
        // ...tomTableColumnFilterPresets.dateMandatory(), // TODO: Fix date filter reading
      },
      {
        accessorKey: 'Container.IsEmpty',
        header: t('cargoStatus', 'Cargo status'),
        accessorFn: row => {
          const isEmpty = row.InboundOrder?.IsEmpty ?? row.OutboundOrder?.IsEmpty
          return isEmpty ? t('empty', 'Empty') : t('full', 'Full')
        },
        Cell: cell => <Typography variant='body2'>{cell.renderedCellValue}</Typography>,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'OutboundCarrierVisit.CarrierName',
        header: t('outboundCarrier', 'Outbound carrier'),
        Cell: cell =>
          cell.row.original.OutboundCarrierVisit?.CarrierName ? (
            <Typography variant='subtitle2'>{cell.renderedCellValue}</Typography>
          ) : (
            EmptyIndicator
          ),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'OutboundCarrierVisit.VesselVisitNumber',
        header: t('outboundCarrierId', 'Outbound carrier ID'),
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'TerminalExit',
        header: t('timeOut', 'Time out'),
        Cell: cell => <TableDateCell date={cell.row.original.TerminalExit} />,
        // ...tomTableColumnFilterPresets.dateOptional(), // TODO: Fix date filter reading
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/container-history',
  )

  const table = useTomTable<TurnoverDto>(
    {
      columns,
      data: containerTurnoverList,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      renderRowActions: ({ row }) => <ContainerHistoryRowActions turnoverDto={row.original} />,
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
